import React from 'react';
import { Chip, makeStyles, useTheme } from '@material-ui/core';
import type { InstanceStatus, ThreadStatus } from './types';

/**
 * Color scheme mirrors the HTML design mock (requirement-lifecycle-mock.html).
 * Two palettes are defined so the badge remains legible in both light and dark
 * Backstage themes.
 */
const STATUS_COLORS_LIGHT: Record<string, { bg: string; fg: string }> = {
  DRAFT: { bg: '#dfe1e6', fg: '#5e6c84' },
  WIP: { bg: '#deebff', fg: '#0747a6' },
  APPROVED: { bg: '#e3fcef', fg: '#006644' },
  DELETED: { bg: '#ffebe6', fg: '#bf2600' },
  SPLIT: { bg: '#fff0b3', fg: '#172b4d' },
  MERGED: { bg: '#eae6ff', fg: '#403294' },
  PENDING_SPLIT: { bg: '#fff0b3', fg: '#974f0c' },
  PENDING_MERGE: { bg: '#fff0b3', fg: '#974f0c' },
  CURRENT: { bg: '#deebff', fg: '#0747a6' },
  PENDING_REVIEW: { bg: '#fff0b3', fg: '#974f0c' },
  SUPERSEDED: { bg: '#dfe1e6', fg: '#5e6c84' },
  REJECTED: { bg: '#ffebe6', fg: '#bf2600' },
  DISCARDED: { bg: '#dfe1e6', fg: '#5e6c84' },
};

const STATUS_COLORS_DARK: Record<string, { bg: string; fg: string }> = {
  DRAFT: { bg: '#3d4555', fg: '#b3bac5' },
  WIP: { bg: '#0d2d6b', fg: '#4c9aff' },
  APPROVED: { bg: '#0a3622', fg: '#57d9a3' },
  DELETED: { bg: '#5c1a0f', fg: '#ff8f73' },
  SPLIT: { bg: '#4a3800', fg: '#ffd700' },
  MERGED: { bg: '#2d2466', fg: '#c0b6f2' },
  PENDING_SPLIT: { bg: '#4a3800', fg: '#ffab00' },
  PENDING_MERGE: { bg: '#4a3800', fg: '#ffab00' },
  CURRENT: { bg: '#0d2d6b', fg: '#4c9aff' },
  PENDING_REVIEW: { bg: '#4a3800', fg: '#ffab00' },
  SUPERSEDED: { bg: '#3d4555', fg: '#b3bac5' },
  REJECTED: { bg: '#5c1a0f', fg: '#ff8f73' },
  DISCARDED: { bg: '#3d4555', fg: '#b3bac5' },
};

const useStyles = makeStyles({
  chip: {
    height: 20,
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 3,
  },
});

interface StatusBadgeProps {
  status: ThreadStatus | InstanceStatus;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const classes = useStyles();
  const theme = useTheme();
  const map =
    theme.palette.type === 'dark' ? STATUS_COLORS_DARK : STATUS_COLORS_LIGHT;
  const fallback =
    theme.palette.type === 'dark'
      ? { bg: '#3d4555', fg: '#b3bac5' }
      : { bg: '#dfe1e6', fg: '#5e6c84' };
  const palette = map[status] ?? fallback;

  return (
    <Chip
      label={status}
      size="small"
      className={classes.chip}
      style={{ backgroundColor: palette.bg, color: palette.fg }}
    />
  );
};

export default StatusBadge;
